import { useEffect, useRef } from 'react';
import './target-cursor.css';

type TargetCursorProps = {
  selector?: string;
  padding?: number;
  idleSize?: number;
  /** Idle rotation, degrees per second. */
  spinSpeed?: number;
};

type CursorState = 'idle' | 'locking' | 'locked' | 'releasing';

const CORNER = 10; // px, base corner size
const LOCK_SCALE = 1.2; // corners grow ~10px → 12px and stroke 1.5 → ~1.8 when locked
const REMEASURE_MS = 250; // safety re-read of the locked target's rect

// Frame-rate independent smoothing: fraction of distance covered per second.
const smooth = (from: number, to: number, rate: number, dt: number) => (
  from + (to - from) * (1 - Math.exp(-rate * dt))
);

// Prevents duplicate cursors if the component is ever mounted twice.
let liveInstances = 0;

/*
 * Performance notes (look unchanged):
 * - Hit-testing (elementFromPoint) only runs when the pointer moved or the page scrolled.
 * - Target rect is read on target change, scroll/resize, and at most every 250ms — not every frame.
 * - Frame size is never animated via width/height; the four corners are positioned with
 *   transforms only, so every frame is compositor work with no layout.
 */
export default function TargetCursor({
  selector = '.cursor-target, [data-cursor-target]',
  padding = 6,
  idleSize = 28,
  spinSpeed = 15,
}: TargetCursorProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const cornerRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const root = rootRef.current;
    const frame = frameRef.current;
    const center = centerRef.current;
    const [tl, tr, bl, br] = cornerRefs.current;
    if (!finePointer.matches || !root || !frame || !center || !tl || !tr || !bl || !br) return undefined;

    liveInstances += 1;
    if (liveInstances > 1) {
      root.style.display = 'none';
      return () => { liveInstances -= 1; };
    }

    document.documentElement.classList.add('target-cursor-active');

    const pointer = { x: 0, y: 0, known: false, inside: false };
    // Box tracked by its center so rotation stays centred without layout.
    const box = { cx: 0, cy: 0, w: idleSize, h: idleSize, rot: 0, scale: 1 };
    let target: Element | null = null;
    let rect: { left: number; top: number; width: number; height: number } | null = null;
    let state: CursorState = 'idle';
    let raf = 0;
    let last = performance.now();
    let lastMeasure = 0;
    let needsHitTest = true;
    let needsMeasure = false;
    let lastOpacity = '';
    let lastTone = '';
    let lockGoal = 0; // upright angle chosen once per lock, so it can't flip mid-settle

    const setState = (next: CursorState) => {
      if (state === next) return;
      state = next;
      root.dataset.state = next;
    };

    const measure = (now: number) => {
      lastMeasure = now;
      needsMeasure = false;
      if (!target) { rect = null; return; }
      const r = target.getBoundingClientRect();
      rect = r.width > 0 && r.height > 0 ? { left: r.left, top: r.top, width: r.width, height: r.height } : null;
    };

    const onMove = (e: PointerEvent) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      if (!pointer.known) {
        pointer.known = true;
        box.cx = e.clientX;
        box.cy = e.clientY;
      }
      pointer.inside = true;
      needsHitTest = true;
    };
    const onScroll = () => { needsHitTest = true; needsMeasure = true; };
    const onOut = (e: MouseEvent) => {
      if (!e.relatedTarget) { pointer.inside = false; needsHitTest = true; } // left the viewport
    };
    const onBlur = () => { pointer.inside = false; needsHitTest = true; };

    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      const opacity = pointer.known && pointer.inside ? '1' : '0';
      if (opacity !== lastOpacity) { root.style.opacity = opacity; lastOpacity = opacity; }

      if (pointer.known) {
        // 1. Hit-test only when something could have changed under the pointer.
        if (needsHitTest) {
          needsHitTest = false;
          let next: Element | null = null;
          let tone = 'primary';
          if (pointer.inside) {
            const el = document.elementFromPoint(pointer.x, pointer.y);
            next = el?.closest(selector) ?? null;
            if (el?.closest('[data-cursor-light]')) tone = 'light';
          }
          if (tone !== lastTone) { root.dataset.tone = tone; lastTone = tone; }
          if (next !== target) {
            target = next;
            measure(now);
            if (!rect) target = null;
            if (target) lockGoal = Math.round(box.rot / 90) * 90;
            setState(target ? 'locking' : 'releasing');
          }
        }

        // 2. Rect: re-read on scroll/resize, plus a light periodic refresh while locked.
        if (target && (needsMeasure || now - lastMeasure > REMEASURE_MS)) {
          measure(now);
          if (!rect) { target = null; setState('releasing'); }
        }

        let tcx = pointer.x;
        let tcy = pointer.y;
        let tw = idleSize;
        let th = idleSize;

        if (rect) {
          // When settling at 90°/270° the frame's local axes are swapped vs. the screen,
          // so swap width/height too — otherwise a wide target gets a tall frame.
          const quarterTurn = Math.abs(((lockGoal % 180) + 180) % 180) === 90;
          tw = (quarterTurn ? rect.height : rect.width) + padding * 2;
          th = (quarterTurn ? rect.width : rect.height) + padding * 2;
          tcx = rect.left + rect.width / 2;
          tcy = rect.top + rect.height / 2;
          // Reticle is 90° symmetric: settle to the nearest upright angle.
          box.rot = smooth(box.rot, lockGoal, 14, dt);
        } else {
          box.rot = (box.rot + spinSpeed * dt) % 360;
        }

        const rate = rect ? 16 : 20;
        box.cx = smooth(box.cx, tcx, rate, dt);
        box.cy = smooth(box.cy, tcy, rate, dt);
        box.w = smooth(box.w, tw, rate, dt);
        box.h = smooth(box.h, th, rate, dt);
        box.scale = smooth(box.scale, rect ? LOCK_SCALE : 1, 18, dt);

        const settled = Math.abs(box.w - tw) < 0.5 && Math.abs(box.h - th) < 0.5
          && Math.abs(box.cx - tcx) < 0.5 && Math.abs(box.cy - tcy) < 0.5;
        if (settled && state === 'locking') setState('locked');
        if (settled && state === 'releasing') setState('idle');

        // 3. Transform-only writes (no layout).
        const hw = box.w / 2;
        const hh = box.h / 2;
        const s = box.scale;
        frame.style.transform = `translate3d(${box.cx}px, ${box.cy}px, 0) rotate(${box.rot}deg)`;
        tl.style.transform = `translate3d(${-hw}px, ${-hh}px, 0) scale(${s})`;
        tr.style.transform = `translate3d(${hw - CORNER}px, ${-hh}px, 0) scale(${s})`;
        bl.style.transform = `translate3d(${-hw}px, ${hh - CORNER}px, 0) scale(${s})`;
        br.style.transform = `translate3d(${hw - CORNER}px, ${hh - CORNER}px, 0) scale(${s})`;
        // Crosshair always tracks the real pointer.
        center.style.transform = `translate3d(${pointer.x}px, ${pointer.y}px, 0)`;
      }

      raf = requestAnimationFrame(tick);
    };

    // TEMP diagnostic: press C to toggle the custom cursor off/on (native cursor returns, loop stops).
    let disabled = false;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'c' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      disabled = !disabled;
      if (disabled) {
        cancelAnimationFrame(raf);
        root.style.display = 'none';
        document.documentElement.classList.remove('target-cursor-active');
      } else {
        root.style.display = '';
        document.documentElement.classList.add('target-cursor-active');
        last = performance.now();
        needsHitTest = true;
        raf = requestAnimationFrame(tick);
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('mouseout', onOut);
    window.addEventListener('blur', onBlur);
    root.dataset.state = 'idle';
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mouseout', onOut);
      window.removeEventListener('blur', onBlur);
      document.documentElement.classList.remove('target-cursor-active');
      liveInstances -= 1;
    };
  }, [selector, padding, idleSize, spinSpeed]);

  const cornerRef = (i: number) => (el: HTMLSpanElement | null) => { cornerRefs.current[i] = el; };

  return (
    <div ref={rootRef} className="target-cursor-root" aria-hidden="true" data-tone="primary" data-state="idle">
      <div ref={frameRef} className="target-cursor-frame">
        <span ref={cornerRef(0)} className="target-corner target-corner-tl" />
        <span ref={cornerRef(1)} className="target-corner target-corner-tr" />
        <span ref={cornerRef(2)} className="target-corner target-corner-bl" />
        <span ref={cornerRef(3)} className="target-corner target-corner-br" />
      </div>
      <div ref={centerRef} className="target-cursor-center">
        <span className="target-cursor-dot" />
      </div>
    </div>
  );
}